import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, ShieldCheck, Lock, CheckCircle2, Mail, PackagePlus, BellRing } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function FormRun() {
  const { slug, id } = useParams();  // /forms/new/:slug  OR  /forms/:id
  const navigate = useNavigate();
  const { user } = useAuth();

  const [template, setTemplate] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [header, setHeader] = useState({});
  const [items, setItems] = useState({});
  const [signoff, setSignoff] = useState({ technician_name: "", client_name: "", user_name: "", comments: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", subject: "", note: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [quickSending, setQuickSending] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [postCompleteOpen, setPostCompleteOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(false);
  const [assetForm, setAssetForm] = useState({ name: "", category: "", serial_number: "", asset_tag: "", assigned_to: "", assigned_email: "" });
  const [categories, setCategories] = useState([]);
  const [creatingAsset, setCreatingAsset] = useState(false);
  const [assetCreated, setAssetCreated] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [alertSent, setAlertSent] = useState(false);

  const submissionId = id || savedId || null;
  const NEW_USER_SLUG = "new-user-setup-2026";
  const SELECT_ALL_SLUGS = [NEW_USER_SLUG, "pc-refresh"];
  const readOnly = submission?.status === "completed";

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, id]);

  const load = async () => {
    setLoading(true);
    try {
      if (submissionId) {
        const { data: sub } = await api.get(`/forms/submissions/${submissionId}`);
        const t = sub.template_snapshot || (await api.get(`/forms/templates/${sub.template_slug}`)).data;
        setSubmission(sub);
        setTemplate(t);
        setHeader(sub.header_inputs || {});
        setItems(sub.items || {});
        setSignoff({
          technician_name: sub.signoff?.technician_name || "",
          client_name: sub.signoff?.client_name || "",
          user_name: sub.signoff?.user_name || "",
          comments: sub.signoff?.comments || "",
        });
      } else if (slug) {
        const { data: t } = await api.get(`/forms/templates/${slug}`);
        setTemplate(t);
        // Prefill some sensible defaults
        setSignoff((s) => ({ ...s, technician_name: user?.name || user?.email || "" }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load form");
      navigate("/forms");
    } finally { setLoading(false); }
  };

  const setHeaderField = (k, v) => setHeader((h) => ({ ...h, [k]: v }));
  const setItemCheck = (k, checked) => setItems((it) => ({ ...it, [k]: { ...(it[k] || {}), checked } }));
  const setItemValue = (k, value) => setItems((it) => ({ ...it, [k]: { ...(it[k] || {}), value } }));

  const setSectionChecks = (section, checked) => {
    setItems((prev) => {
      const next = { ...prev };
      for (const it of section.items) {
        if (it.kind === "check") next[it.key] = { ...(next[it.key] || {}), checked };
      }
      return next;
    });
  };

  const stats = useMemo(() => {
    if (!template) return { total: 0, done: 0 };
    let total = 0, done = 0;
    for (const s of template.sections) for (const it of s.items) {
      if (it.kind === "check") { total++; if (items[it.key]?.checked) done++; }
    }
    return { total, done };
  }, [template, items]);

  const missingHeaderKeys = useMemo(() => {
    if (!template) return [];
    return template.header_inputs
      .filter((h) => h.required && !(header[h.key] || "").toString().trim())
      .map((h) => h.label);
  }, [template, header]);

  const payload = () => ({
    template_slug: template.slug,
    header_inputs: header,
    items,
    signoff,
    complete: false,
  });

  const save = async ({ complete = false } = {}) => {
    if (complete) {
      if (missingHeaderKeys.length) {
        toast.error(`Fill required fields: ${missingHeaderKeys.join(", ")}`);
        setCompleteOpen(false);
        return;
      }
      if (!signoff.technician_name.trim()) {
        toast.error("Technician sign-off name is required to complete this form");
        setCompleteOpen(false);
        return;
      }
      if (stats.done < stats.total) {
        toast.error(`Complete all ${stats.total} checkpoints first (${stats.done} done)`);
        setCompleteOpen(false);
        return;
      }
    }
    setSaving(true);
    try {
      const body = { ...payload(), complete };
      if (submissionId) {
        const { data } = await api.put(`/forms/submissions/${submissionId}`, body);
        setSubmission(data);
        toast.success(complete ? "Form completed and locked" : "Draft saved");
        if (complete) {
          if (template.slug === NEW_USER_SLUG) setPostCompleteOpen(true);
          else navigate("/forms");
        }
      } else {
        const { data } = await api.post("/forms/submissions", body);
        toast.success(complete ? "Form completed and locked" : "Draft saved");
        if (complete && template.slug === NEW_USER_SLUG) {
          setSubmission(data);
          setSavedId(data.id);
          setPostCompleteOpen(true);
        } else {
          navigate(complete ? "/forms" : `/forms/${data.id}`);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); setCompleteOpen(false); }
  };

  const hasComments = useMemo(() => {
    return Object.entries(items).some(([k, v]) => k.endsWith("_comments") && (v?.value || "").trim());
  }, [items]);

  const HELPDESK_EMAIL = "helpdesk@rheel.co.nz";

  const openEmailModal = () => {
    setEmailForm({
      to: HELPDESK_EMAIL,
      subject: "",
      note: "",
    });
    setEmailOpen(true);
  };

  const quickSendToHelpdesk = async () => {
    if (!submissionId) { toast.error("Save the form first"); return; }
    setQuickSending(true);
    try {
      const { data } = await api.post(`/forms/submissions/${submissionId}/email-comments`, {
        to: [HELPDESK_EMAIL], subject: "", note: "",
      });
      toast.success(`Sent to ${data.sent_to.join(", ")} · ${data.section_count} section${data.section_count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally { setQuickSending(false); }
  };

  const sendCommentsEmail = async () => {
    const to = emailForm.to.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (to.length === 0) { toast.error("Enter at least one recipient"); return; }
    if (!submissionId) { toast.error("Save the form first"); return; }
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/forms/submissions/${submissionId}/email-comments`, {
        to, subject: emailForm.subject, note: emailForm.note,
      });
      toast.success(`Sent to ${data.sent_to.join(", ")} · ${data.section_count} section${data.section_count === 1 ? "" : "s"}`);
      setEmailOpen(false);
      setEmailForm({ to: "", subject: "", note: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally { setSendingEmail(false); }
  };

  const openAssetDialog = async () => {
    setAssetForm({
      name: header.device_make_model || "",
      category: "",
      serial_number: header.serial_number || "",
      asset_tag: header.asset_tag || "",
      assigned_to: header.user_name || "",
      assigned_email: header.user_email || "",
    });
    setAssetOpen(true);
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } catch {
      setCategories([]);
    }
  };

  const createAssetFromForm = async () => {
    if (!assetForm.name.trim()) { toast.error("Asset name is required"); return; }
    if (!assetForm.category) { toast.error("Select a category"); return; }
    setCreatingAsset(true);
    try {
      const { data } = await api.post(`/forms/submissions/${submissionId}/create-asset`, assetForm);
      toast.success(`Asset created: ${data.name}${data.asset_tag ? ` (${data.asset_tag})` : ""}`);
      setAssetCreated(true);
      setAssetOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create asset");
    } finally { setCreatingAsset(false); }
  };

  const sendHelpdeskAlert = async () => {
    setSendingAlert(true);
    try {
      const { data } = await api.post(`/forms/submissions/${submissionId}/notify-helpdesk`);
      toast.success(`Alert sent to ${data.sent_to.join(", ")}`);
      setAlertSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send alert");
    } finally { setSendingAlert(false); }
  };

  if (loading || !template) {
    return <div className="p-8 font-mono text-xs text-[#7A7A7A]">Loading…</div>;
  }

  const pct = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  return (
    <div data-testid="form-run-page">
      <PageHeader
        eyebrow={`[FORM / ${submission?.status?.toUpperCase() || "NEW"}]`}
        title={template.name}
        subtitle={template.description}
        actions={
          <>
            <Link to="/forms" data-testid="forms-back" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#5A5A5A] hover:text-[#0F0F0F] mr-3">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
            {!readOnly && (
              <>
                <Button
                  data-testid="save-draft-btn"
                  variant="outline"
                  onClick={() => save({ complete: false })}
                  disabled={saving}
                  className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase mr-2"
                >
                  <Save className="w-3.5 h-3.5 mr-2" /> Save Draft
                </Button>
                <Button
                  data-testid="complete-btn"
                  onClick={() => setCompleteOpen(true)}
                  disabled={saving}
                  className="rounded-none bg-[#2D7A4F] hover:bg-[#236340] text-white font-mono text-xs tracking-wider uppercase"
                >
                  <ShieldCheck className="w-3.5 h-3.5 mr-2" /> Complete & Sign Off
                </Button>
              </>
            )}
            {submissionId && hasComments && (
              <>
                <Button
                  data-testid="quick-email-helpdesk-btn"
                  onClick={quickSendToHelpdesk}
                  disabled={quickSending}
                  className="ml-2 rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase disabled:opacity-60"
                >
                  <Mail className="w-3.5 h-3.5 mr-2" /> {quickSending ? "Sending…" : "Email Helpdesk"}
                </Button>
                <Button
                  data-testid="email-comments-btn"
                  variant="outline"
                  onClick={openEmailModal}
                  className="ml-2 rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase"
                >
                  <Mail className="w-3.5 h-3.5 mr-2" /> Email…
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl">
        {/* Status banner */}
        {readOnly && (
          <div data-testid="locked-banner" className="border border-[#2D7A4F]/40 bg-[#E8F1EB] px-5 py-4 flex items-start gap-3">
            <Lock className="w-4 h-4 text-[#2D7A4F] mt-0.5" strokeWidth={1.75} />
            <div>
              <div className="uppercase-label text-[#2D7A4F]">Locked audit record</div>
              <div className="text-sm text-[#3A3A3A] mt-1">
                Completed on <strong>{fmtDate(submission?.completed_at)}</strong> by <strong>{submission?.completed_by}</strong>.
                This form is read-only and preserved for auditing.
              </div>
            </div>
          </div>
        )}

        {/* Progress */}
        <div className="border border-[#E5E0D5] bg-white px-5 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="uppercase-label">Progress</div>
            <div className="font-mono text-sm text-[#0F0F0F] mt-1">
              {stats.done} of {stats.total} checkpoints complete
            </div>
          </div>
          <div className="flex-1 max-w-md">
            <div className="h-2 bg-[#E5E0D5] relative">
              <div
                data-testid="progress-bar"
                className={`absolute inset-y-0 left-0 ${pct === 100 ? "bg-[#2D7A4F]" : "bg-[#F37B2A]"} transition-all`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
          <div className="font-mono text-sm text-[#0F0F0F] w-12 text-right">{pct}%</div>
        </div>

        {/* Header inputs */}
        <div className="border border-[#E5E0D5] bg-white">
          <div className="px-5 py-3 border-b border-[#E5E0D5]">
            <div className="uppercase-label">Details</div>
            <div className="text-xs text-[#7A7A7A]">User, device and requirements captured before deployment.</div>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {template.header_inputs.map((h) => (
              <div key={h.key}>
                <Label className="uppercase-label">
                  {h.label}{h.required && <span className="text-[#B91C1C] ml-0.5">*</span>}
                </Label>
                <Input
                  data-testid={`header-${h.key}`}
                  value={header[h.key] || ""}
                  onChange={(e) => setHeaderField(h.key, e.target.value)}
                  placeholder={h.placeholder}
                  disabled={readOnly}
                  className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Sections */}
        {template.sections.map((section) => {
          const secChecks = section.items.filter((i) => i.kind === "check");
          const secDone = secChecks.filter((i) => items[i.key]?.checked).length;
          const secComplete = secChecks.length > 0 && secDone === secChecks.length;
          return (
            <div key={section.key} data-testid={`section-${section.key}`} className="border border-[#E5E0D5] bg-white">
              <div className="px-5 py-3 border-b border-[#E5E0D5] flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {secComplete && <CheckCircle2 className="w-4 h-4 text-[#2D7A4F] flex-shrink-0" strokeWidth={2} />}
                  <div className="min-w-0">
                    <div className="uppercase-label">{section.heading}</div>
                  </div>
                </div>
                {secChecks.length > 0 && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {!readOnly && SELECT_ALL_SLUGS.includes(template.slug) && (
                      <button
                        type="button"
                        data-testid={`section-select-all-${section.key}`}
                        onClick={() => setSectionChecks(section, !secComplete)}
                        className="font-mono text-[10px] uppercase tracking-wider text-[#F37B2A] hover:text-[#D86A1F] underline underline-offset-2"
                      >
                        {secComplete ? "Clear All" : "Select All"}
                      </button>
                    )}
                    <span className={`font-mono text-[10px] uppercase tracking-wider ${secComplete ? "text-[#2D7A4F]" : "text-[#7A7A7A]"}`}>
                      {secDone}/{secChecks.length}
                    </span>
                  </div>
                )}
              </div>
              <div className="divide-y divide-[#E5E0D5]">
                {section.items.map((it) => {
                  if (it.kind === "header") {
                    return (
                      <div key={it.key} data-testid={`subheader-${it.key}`} className="px-5 py-2 bg-[#F0EDE3]">
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#5A5A5A]">{it.label}</span>
                      </div>
                    );
                  }
                  if (it.kind === "check") {
                    const checked = !!items[it.key]?.checked;
                    return (
                      <label
                        key={it.key}
                        className={`flex items-start gap-3 px-5 py-3 hover:bg-[#FAF7F0] cursor-pointer ${readOnly ? "cursor-default" : ""}`}
                        onClick={(e) => { if (readOnly) e.preventDefault(); }}
                      >
                        <Checkbox
                          data-testid={`check-${it.key}`}
                          checked={checked}
                          onCheckedChange={(v) => !readOnly && setItemCheck(it.key, !!v)}
                          disabled={readOnly}
                          className="mt-0.5 rounded-none border-[#D6CFC1] data-[state=checked]:bg-[#2D7A4F] data-[state=checked]:border-[#2D7A4F]"
                        />
                        <span className={`text-sm ${checked ? "text-[#5A5A5A] line-through" : "text-[#1F1F1F]"}`}>{it.label}</span>
                      </label>
                    );
                  }
                  // 'input' kind
                  return (
                    <div key={it.key} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="text-sm text-[#3A3A3A] sm:w-2/5">{it.label}</div>
                      <Input
                        data-testid={`input-${it.key}`}
                        value={items[it.key]?.value || ""}
                        onChange={(e) => setItemValue(it.key, e.target.value)}
                        disabled={readOnly}
                        className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm flex-1 disabled:opacity-70"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Sign-off */}
        <div className="border border-[#E5E0D5] bg-white">
          <div className="px-5 py-3 border-b border-[#E5E0D5]">
            <div className="uppercase-label">Sign-Off</div>
            <div className="text-xs text-[#7A7A7A]">
              Names typed below are recorded as electronic signatures. On completion the form is timestamped and locked.
            </div>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="uppercase-label">Technician <span className="text-[#B91C1C]">*</span></Label>
              <Input
                data-testid="signoff-technician"
                value={signoff.technician_name}
                onChange={(e) => setSignoff({ ...signoff, technician_name: e.target.value })}
                placeholder="Type your full name"
                disabled={readOnly}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm disabled:opacity-70"
              />
            </div>
            <div>
              <Label className="uppercase-label">Client Rep</Label>
              <Input
                data-testid="signoff-client"
                value={signoff.client_name}
                onChange={(e) => setSignoff({ ...signoff, client_name: e.target.value })}
                placeholder="Client signatory (optional)"
                disabled={readOnly}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm disabled:opacity-70"
              />
            </div>
            <div>
              <Label className="uppercase-label">User</Label>
              <Input
                data-testid="signoff-user"
                value={signoff.user_name}
                onChange={(e) => setSignoff({ ...signoff, user_name: e.target.value })}
                placeholder="End user (optional)"
                disabled={readOnly}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm disabled:opacity-70"
              />
            </div>
            <div className="sm:col-span-3">
              <Label className="uppercase-label">Comments</Label>
              <Textarea
                data-testid="signoff-comments"
                value={signoff.comments}
                onChange={(e) => setSignoff({ ...signoff, comments: e.target.value })}
                rows={3}
                placeholder="Notes, exceptions, follow-ups…"
                disabled={readOnly}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm disabled:opacity-70"
              />
            </div>
          </div>

          {readOnly && (
            <div className="px-5 pb-5">
              <div className="font-mono text-xs text-[#5A5A5A] space-y-1 border-t border-[#E5E0D5] pt-4">
                <div>Completed at: <span className="text-[#0F0F0F]">{fmtDate(submission.completed_at)}</span></div>
                <div>Completed by: <span className="text-[#0F0F0F]">{submission.completed_by}</span></div>
                <div>Started by: <span className="text-[#0F0F0F]">{submission.created_by}</span> on {fmtDate(submission.created_at)}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Completion confirmation */}
      <AlertDialog open={completeOpen} onOpenChange={(o) => !o && setCompleteOpen(false)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Complete and sign off?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">
              This will lock the form for auditing. It cannot be edited afterwards. Your name (<strong>{signoff.technician_name || "—"}</strong>) will be recorded as the technician sign-off with today&apos;s timestamp.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-complete-btn"
              disabled={saving}
              onClick={(e) => { e.preventDefault(); save({ complete: true }); }}
              className="rounded-none bg-[#2D7A4F] hover:bg-[#236340] text-white"
            >{saving ? "Saving…" : "Sign off & lock"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={emailOpen} onOpenChange={(o) => !o && setEmailOpen(false)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Email section comments</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A] text-sm">
              Only the section comment fields with content are included. Sent via Microsoft 365 from <span className="font-mono">assets@misco.kiwi</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="uppercase-label">To <span className="text-[#B91C1C]">*</span></Label>
              <Input
                data-testid="email-to-input"
                value={emailForm.to}
                onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                placeholder="one@example.com, two@example.com"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
                autoFocus
              />
              <div className="mt-1 font-mono text-[10px] text-[#7A7A7A]">Separate multiple recipients with commas or spaces.</div>
            </div>
            <div>
              <Label className="uppercase-label">Subject</Label>
              <Input
                data-testid="email-subject-input"
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                placeholder={`${template.name} — Comments`}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">Additional note</Label>
              <Textarea
                data-testid="email-note-input"
                value={emailForm.note}
                onChange={(e) => setEmailForm({ ...emailForm, note: e.target.value })}
                rows={3}
                placeholder="Optional message shown above the comments"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="email-cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="email-send-btn"
              disabled={sendingEmail || !emailForm.to.trim()}
              onClick={(e) => { e.preventDefault(); sendCommentsEmail(); }}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white disabled:opacity-50"
            >{sendingEmail ? "Sending…" : "Send"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Post-completion next steps (New User Setup only) */}
      <Dialog open={postCompleteOpen} onOpenChange={(o) => { if (!o) { setPostCompleteOpen(false); navigate("/forms"); } }}>
        <DialogContent data-testid="post-complete-dialog" className="rounded-none bg-white border-[#D6CFC1] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-medium tracking-tight flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#2D7A4F]" /> Setup form completed
            </DialogTitle>
            <DialogDescription className="text-[#5A5A5A] text-sm">
              Next steps for <strong>{header.user_name || "the new user"}</strong>
              {header.client ? <> at <strong>{header.client}</strong></> : null}:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <button
              data-testid="add-asset-option-btn"
              onClick={openAssetDialog}
              disabled={assetCreated}
              className="w-full flex items-start gap-3 border border-[#E5E0D5] px-4 py-3 text-left hover:bg-[#FAF7F0] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <PackagePlus className="w-4 h-4 text-[#F37B2A] mt-0.5 flex-shrink-0" strokeWidth={1.75} />
              <span>
                <span className="block text-sm font-medium text-[#0F0F0F]">{assetCreated ? "Asset added ✓" : "Add device as a new asset"}</span>
                <span className="block text-xs text-[#7A7A7A] mt-0.5">
                  Pre-filled with {header.device_make_model || "device"}{header.serial_number ? ` · ${header.serial_number}` : ""}
                </span>
              </span>
            </button>
            <button
              data-testid="helpdesk-alert-option-btn"
              onClick={sendHelpdeskAlert}
              disabled={sendingAlert || alertSent}
              className="w-full flex items-start gap-3 border border-[#E5E0D5] px-4 py-3 text-left hover:bg-[#FAF7F0] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <BellRing className="w-4 h-4 text-[#F37B2A] mt-0.5 flex-shrink-0" strokeWidth={1.75} />
              <span>
                <span className="block text-sm font-medium text-[#0F0F0F]">
                  {alertSent ? "Helpdesk alerted ✓" : sendingAlert ? "Sending alert…" : "Email helpdesk alert"}
                </span>
                <span className="block text-xs text-[#7A7A7A] mt-0.5">
                  Notify helpdesk@rheel.co.nz that a new user setup for {header.client || "this client"} was created
                </span>
              </span>
            </button>
          </div>
          <DialogFooter>
            <Button
              data-testid="post-complete-done-btn"
              onClick={() => { setPostCompleteOpen(false); navigate("/forms"); }}
              className="rounded-none bg-[#2D7A4F] hover:bg-[#236340] text-white font-mono text-xs tracking-wider uppercase"
            >Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create asset dialog */}
      <Dialog open={assetOpen} onOpenChange={(o) => !o && setAssetOpen(false)}>
        <DialogContent data-testid="create-asset-dialog" className="rounded-none bg-white border-[#D6CFC1] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-medium tracking-tight">Add device as asset</DialogTitle>
            <DialogDescription className="text-[#5A5A5A] text-sm">
              Review the details, pick a category, and the device will be added to the asset register for the active organisation.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2">
              <Label className="uppercase-label">Asset Name <span className="text-[#B91C1C]">*</span></Label>
              <Input
                data-testid="asset-name-input"
                value={assetForm.name}
                onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
                placeholder="Device make & model"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">Category <span className="text-[#B91C1C]">*</span></Label>
              <Select value={assetForm.category} onValueChange={(v) => setAssetForm({ ...assetForm, category: v })}>
                <SelectTrigger data-testid="asset-category-select" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.name} data-testid={`asset-category-${c.name}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="uppercase-label">Asset Tag</Label>
              <Input
                data-testid="asset-tag-input"
                value={assetForm.asset_tag}
                onChange={(e) => setAssetForm({ ...assetForm, asset_tag: e.target.value })}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">Serial Number</Label>
              <Input
                data-testid="asset-serial-input"
                value={assetForm.serial_number}
                onChange={(e) => setAssetForm({ ...assetForm, serial_number: e.target.value })}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">Assigned To</Label>
              <Input
                data-testid="asset-assigned-input"
                value={assetForm.assigned_to}
                onChange={(e) => setAssetForm({ ...assetForm, assigned_to: e.target.value })}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="uppercase-label">Assigned Email</Label>
              <Input
                data-testid="asset-email-input"
                value={assetForm.assigned_email}
                onChange={(e) => setAssetForm({ ...assetForm, assigned_email: e.target.value })}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="asset-cancel-btn"
              onClick={() => setAssetOpen(false)}
              className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6] font-mono text-xs tracking-wider uppercase"
            >Cancel</Button>
            <Button
              data-testid="asset-create-btn"
              disabled={creatingAsset || !assetForm.name.trim() || !assetForm.category}
              onClick={createAssetFromForm}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase disabled:opacity-50"
            >{creatingAsset ? "Creating…" : "Create Asset"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
